import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Modal,
  Form,
  Select,
  InputNumber,
  Typography,
  Alert,
  Divider,
} from "antd";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../../variables/constants";

const { Text } = Typography;

// --- Helpers de bornes (le modèle stocke des tableaux d'entiers triés) ---
const toInts = (arr) =>
  (arr || [])
    .map((s) => Number(String(s).trim()))
    .filter((n) => Number.isFinite(n));

const normalize = (arr) => [...toInts(arr)].sort((a, b) => a - b);

const arraysEqual = (a, b) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};

// Aperçu lisible d'une tranche : 2 bornes = « X – Y », sinon liste des bornes.
export const trancheLabel = (arr, suffix = "") => {
  const n = normalize(arr);
  if (!n.length) return "—";
  if (n.length === 2) return `${n[0]}${suffix} – ${n[1]}${suffix}`;
  return n.map((v) => `${v}${suffix}`).join(", ");
};

const AddValeurPointIPV2 = ({
  open,
  onClose,
  editMode = false,
  selectedItem = null,
  items = [],
  presetTaux = null,
  presetAge = null,
  onAdd,
  onUpdate,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({ trancheTauxIp: [], trancheAgeBlesse: [], valeur: null });

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${TOKEN}`, "Content-Type": "application/json" }),
    []
  );

  useEffect(() => {
    if (!open) return;
    let initial;
    if (editMode && selectedItem) {
      initial = {
        trancheTauxIp: (selectedItem.trancheTauxIp || []).map(String),
        trancheAgeBlesse: (selectedItem.trancheAgeBlesse || []).map(String),
        valeur: selectedItem.valeur ?? null,
      };
    } else {
      initial = {
        trancheTauxIp: (presetTaux || []).map(String),
        trancheAgeBlesse: (presetAge || []).map(String),
        valeur: null,
      };
    }
    form.setFieldsValue(initial);
    setValues(initial);
  }, [open, editMode, selectedItem, presetTaux, presetAge, form]);

  const isDuplicate = (tauxArr, ageArr) => {
    const nTaux = normalize(tauxArr);
    const nAge = normalize(ageArr);
    return items?.some((it) => {
      if (editMode && selectedItem && it.valeurPointIpId === selectedItem.valeurPointIpId) return false;
      return (
        arraysEqual(normalize(it.trancheTauxIp || []), nTaux) &&
        arraysEqual(normalize(it.trancheAgeBlesse || []), nAge)
      );
    });
  };

  const onValuesChange = (_, all) => setValues(all);

  const handleSubmit = async () => {
    let fv;
    try {
      fv = await form.validateFields();
    } catch {
      return;
    }
    const trancheTauxIp = normalize(fv.trancheTauxIp);
    const trancheAgeBlesse = normalize(fv.trancheAgeBlesse);
    const valeur = Number(fv.valeur);

    if (trancheTauxIp.some((n) => n < 0) || trancheAgeBlesse.some((n) => n < 0)) {
      toast.warning("Les bornes doivent être des entiers positifs ou nuls.");
      return;
    }
    if (!editMode && isDuplicate(trancheTauxIp, trancheAgeBlesse)) {
      toast.error("Cette combinaison de tranches existe déjà.");
      return;
    }

    setLoading(true);
    const base = { trancheTauxIp, trancheAgeBlesse, valeur };
    try {
      if (editMode) {
        const payload = { ...selectedItem, ...base };
        const res = await axios.put(`${API_URL}/api/valeur-point-ip`, payload, { headers });
        toast.success("Valeur du point IP modifiée.");
        onUpdate && onUpdate(res.data);
      } else {
        const res = await axios.post(`${API_URL}/api/valeur-point-ip`, base, { headers });
        toast.success("Valeur du point IP ajoutée.");
        onAdd && onAdd(res.data);
      }
      onClose && onClose();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  const dupLive =
    values.trancheTauxIp?.length &&
    values.trancheAgeBlesse?.length &&
    isDuplicate(values.trancheTauxIp, values.trancheAgeBlesse);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={editMode ? "Enregistrer" : "Ajouter"}
      cancelText="Annuler"
      confirmLoading={loading}
      okButtonProps={{ disabled: !editMode && !!dupLive }}
      title={editMode ? "Modifier une valeur du point IP" : "Nouvelle valeur du point IP"}
      destroyOnClose
      maskClosable={false}
      width={560}
    >
      <Text type="secondary">
        Barème utilisé pour l'indemnisation de l'incapacité permanente (IPP) : la valeur du point
        dépend de la tranche de taux d'IP et de la tranche d'âge du blessé.
      </Text>
      <Divider style={{ margin: "16px 0" }} />

      <Form form={form} layout="vertical" onValuesChange={onValuesChange} requiredMark="optional">
        <Form.Item
          label="Tranche de taux d'IP (bornes en %)"
          name="trancheTauxIp"
          tooltip="Saisissez les bornes de la tranche, ex. 10 puis 20 pour « 10 % – 20 % ». Validez chaque borne par Entrée."
          rules={[{ required: true, message: "Saisissez au moins une borne de taux d'IP." }]}
        >
          <Select
            mode="tags"
            tokenSeparators={[",", " ", ";"]}
            placeholder="ex : 10, 20"
            suffixIcon={null}
            open={false}
          />
        </Form.Item>
        {values.trancheTauxIp?.length ? (
          <Text type="secondary" style={{ display: "block", marginTop: -12, marginBottom: 12 }}>
            Interprété : <b>{trancheLabel(values.trancheTauxIp, " %")}</b>
          </Text>
        ) : null}

        <Form.Item
          label="Tranche d'âge du blessé (bornes en années)"
          name="trancheAgeBlesse"
          tooltip="Saisissez les bornes de la tranche, ex. 18 puis 25 pour « 18 – 25 ans »."
          rules={[{ required: true, message: "Saisissez au moins une borne d'âge." }]}
        >
          <Select
            mode="tags"
            tokenSeparators={[",", " ", ";"]}
            placeholder="ex : 18, 25"
            suffixIcon={null}
            open={false}
          />
        </Form.Item>
        {values.trancheAgeBlesse?.length ? (
          <Text type="secondary" style={{ display: "block", marginTop: -12, marginBottom: 12 }}>
            Interprété : <b>{trancheLabel(values.trancheAgeBlesse, " ans")}</b>
          </Text>
        ) : null}

        <Form.Item
          label="Valeur du point IP (FCFA)"
          name="valeur"
          rules={[
            { required: true, message: "Saisissez la valeur du point." },
            {
              validator: (_, v) =>
                v > 0 ? Promise.resolve() : Promise.reject(new Error("La valeur doit être strictement positive.")),
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            step={100}
            controls
            formatter={(v) => (v == null ? "" : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " "))}
            parser={(v) => (v || "").replace(/\s/g, "")}
            addonAfter="FCFA"
            placeholder="ex : 250 000"
          />
        </Form.Item>

        {dupLive ? (
          <Alert
            type="warning"
            showIcon
            message="Cette combinaison de tranches existe déjà."
            description="Modifiez l'entrée existante plutôt que d'en créer un doublon."
          />
        ) : null}
      </Form>
    </Modal>
  );
};

export default AddValeurPointIPV2;
